import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CheckOutlined,
  LeftOutlined,
  PlayCircleOutlined,
  RightOutlined
} from "@ant-design/icons"
import { Button, Flex, Typography, theme } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useNavigate, useParams } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { generateDocumentTitle } from "../../constants/document-title.ts"
import { RoutesPaths } from "../../constants/routes.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { isGuideLessonAccessible } from "./content/guide-permissions.ts"
import {
  findAreaForModule,
  getAccessibleLessonsForArea
} from "./content/learning-areas.ts"
import { getGuideLesson, getLessonsForModule, getRelatedLessons } from "./content/lessons.ts"
import { getGuideModule } from "./content/modules.ts"
import { useGuideProgress } from "./hooks/use-guide-progress.ts"
import { useGuideTour } from "./tour/GuideTourContext.tsx"
import { useProductTour } from "./tour/ProductTourContext.tsx"

const { Title, Text, Paragraph } = Typography

export const GuideLessonPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { lessonId } = useParams<{ lessonId: string }>()
  const { isLessonComplete, setLessonComplete } = useGuideProgress()
  const { startTour, activeTour: activeGuideTour, skip: skipGuideTour } = useGuideTour()
  const {
    start: startProductTour,
    isActive: productTourActive,
    skip: stopProductTour
  } = useProductTour()
  const { token } = theme.useToken()
  const pages = useAppSelector((state) => state.auth?.permissions?.pages)
  const codes = useAppSelector((state) => state.auth?.permissions?.codes)

  const lessonMatch = getGuideLesson(lessonId ?? "")
  // A lesson id that resolves to a real lesson the user has no permission
  // for is treated exactly like an id that doesn't exist — direct-URL entry
  // must never render (or even briefly flash) content a `requiredPage`
  // gate would hide everywhere else in the Guide.
  const lesson =
    lessonMatch && isGuideLessonAccessible(lessonMatch, pages, codes) ? lessonMatch : null

  useEffect(() => {
    if (lesson) generateDocumentTitle(t(lesson.titleKey))
  }, [lesson, t])

  // Same redirect + neutral message whether the id never existed or exists
  // but is gated — confirming "it exists, you just can't see it" is its own
  // leak.
  if (!lesson) return <Navigate to={`${RoutesPaths.guide.academy}?unavailable=1`} replace />

  const module = getGuideModule(lesson.moduleSlug)
  const area = findAreaForModule(lesson.moduleSlug)
  // Paging spans the whole area when the lesson belongs to one of the six
  // browsable areas, falling back to just its own module's lessons (e.g.
  // start-here, which isn't part of any area) — never just itself. Always
  // permission-filtered: an inaccessible sibling must never appear as a
  // Next/Previous target, since clicking it would only bounce back here.
  const siblings = area
    ? getAccessibleLessonsForArea(area, pages, codes)
    : getLessonsForModule(lesson.moduleSlug).filter((sibling) =>
        isGuideLessonAccessible(sibling, pages, codes)
      )
  const position = siblings.findIndex((sibling) => sibling.id === lesson.id)
  const previous = position > 0 ? siblings[position - 1] : null
  const next = position >= 0 && position < siblings.length - 1 ? siblings[position + 1] : null
  const complete = isLessonComplete(lesson.id)
  const relatedLessons = getRelatedLessons(lesson)

  const backTarget = area
    ? RoutesPaths.guide.module(area.id)
    : module
      ? RoutesPaths.guide.module(module.slug)
      : RoutesPaths.guide.academy

  // The two tour engines are independent state machines with no shared
  // "only one may run" guard of their own — starting one here must stop
  // the other first, or both overlays mount at once (same fixed z-index,
  // two sets of Next/Back/Skip controls).
  const handleTakeTour = () => {
    if (lesson.relatedProductTourStepId) {
      if (activeGuideTour) skipGuideTour()
      startProductTour(lesson.relatedProductTourStepId)
      return
    }
    if (lesson.relatedTourId) {
      if (productTourActive) stopProductTour()
      startTour(lesson.relatedTourId)
    }
  }

  return (
    <Flex vertical gap="large" className="mx-auto max-w-3xl pb-8">
      <Link to={backTarget} className="flex items-center gap-1 text-sm text-gray-500">
        <ArrowLeftOutlined /> {area ? t(area.titleKey) : module ? t(module.titleKey) : null}
      </Link>

      <Flex vertical gap={16}>
        <Flex vertical gap={4}>
          {module && (
            <Text
              className="text-xs font-semibold tracking-widest uppercase"
              style={{ color: token.colorPrimary }}
            >
              {t(module.titleKey)} · {t("guide.lesson.duration", { minutes: lesson.durationMinutes })}
            </Text>
          )}
          <Title level={2} className="!mb-0">
            {t(lesson.titleKey)}
          </Title>
          <Text type="secondary" className="text-base">
            {t(lesson.descriptionKey)}
          </Text>
        </Flex>

        {(lesson.relatedProductTourStepId || lesson.relatedTourId) && (
          <Button
            type="primary"
            size="large"
            icon={<PlayCircleOutlined />}
            className="self-start"
            onClick={handleTakeTour}
          >
            {t("guide.contextualHelp.common.takeATour")}
          </Button>
        )}
      </Flex>

      {lesson.learningPointKeys && lesson.learningPointKeys.length > 0 && (
        <CardComponent style={{ background: token.colorFillQuaternary }} className="border-none">
          <Flex vertical gap={8}>
            <Text strong className="text-sm">
              {t("guide.lesson.whatYoullLearn")}
            </Text>
            {lesson.learningPointKeys.map((key) => (
              <Flex key={key} align="start" gap={8}>
                <CheckCircleFilled className="mt-1 text-xs" style={{ color: token.colorPrimary }} />
                <Text className="text-sm">{t(key)}</Text>
              </Flex>
            ))}
          </Flex>
        </CardComponent>
      )}

      <Flex vertical gap={12}>
        {lesson.bodyParagraphKeys.map((key) => (
          <Paragraph key={key} className="!mb-0 text-base">
            {t(key)}
          </Paragraph>
        ))}
      </Flex>

      {relatedLessons.length > 0 && (
        <Flex vertical gap={8}>
          <Text strong className="text-sm">
            {t("guide.lesson.relatedGuides")}
          </Text>
          <Flex vertical gap={4}>
            {relatedLessons.map((related) => (
              <Link
                key={related.id}
                to={RoutesPaths.guide.lesson(related.id)}
                className="flex items-center gap-1 text-sm"
                style={{ color: token.colorPrimary }}
              >
                {t(related.titleKey)} <RightOutlined className="text-xs" />
              </Link>
            ))}
          </Flex>
        </Flex>
      )}

      <Flex justify="space-between" align="center" className="mt-4 border-t border-border pt-4">
        <Button
          type={complete ? "default" : "primary"}
          icon={complete ? <CheckOutlined /> : undefined}
          onClick={() => setLessonComplete(lesson.id, !complete)}
        >
          {complete ? t("guide.lesson.markIncomplete") : t("guide.lesson.markComplete")}
        </Button>

        <Flex gap={8}>
          <Button
            icon={<LeftOutlined />}
            disabled={!previous}
            onClick={() => previous && navigate(RoutesPaths.guide.lesson(previous.id))}
          >
            {t("guide.lesson.previous")}
          </Button>
          <Button
            icon={<RightOutlined />}
            iconPosition="end"
            disabled={!next}
            onClick={() => next && navigate(RoutesPaths.guide.lesson(next.id))}
          >
            {t("guide.lesson.next")}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  )
}
